/**
 * Строка формы: подпись + контрол + последствие (§2.2 роль `.t-label`, §7, П6).
 *
 * Собирает три вещи, которые в спеке всегда ходят вместе: подпись поля,
 * сам контрол и серую строку-последствие под ним. Ошибка и последствие — разные
 * слоты: ошибка говорит «так нельзя», последствие — «вот что случится».
 *
 * `htmlFor` обязателен, когда контрол — одно поле: подпись без связи с полем
 * не работает ни для мыши, ни для скринридера. Если внутри несколько контролов,
 * вместо `htmlFor` передаётся `groupLabel` и строка становится `role="group"`.
 */
import { useId } from 'react'
import type { ReactNode } from 'react'
import './FieldRow.css'

export interface FieldRowProps {
  label: ReactNode
  children: ReactNode
  /** id контрола: подпись связывается с ним. */
  htmlFor?: string
  /** Группа контролов вместо одного поля: подпись становится именем группы. */
  group?: boolean
  /** Серая строка-последствие под контролом (П6). */
  consequence?: ReactNode
  /** Текст ошибки под контролом. */
  error?: string
  /** Поле «моей зоны ответственности» (§4.9). */
  mine?: boolean
  mineLabel?: string
  /** stacked — подпись сверху (по умолчанию), inline — подпись слева. */
  layout?: 'stacked' | 'inline'
  className?: string
}

export function FieldRow({
  label,
  children,
  htmlFor,
  group = false,
  consequence,
  error,
  mine = false,
  mineLabel = 'Ваше поле: за него отвечаете вы',
  layout = 'stacked',
  className,
}: FieldRowProps) {
  const reactId = useId()
  const labelId = `field-row-label${reactId}`
  const errorId = `field-row-error${reactId}`

  const classes = ['field-row']
  if (layout === 'inline') classes.push('field-row--inline')
  if (className) classes.push(className)

  return (
    <div
      className={classes.join(' ')}
      role={group ? 'group' : undefined}
      aria-labelledby={group ? labelId : undefined}
    >
      {group ? (
        <span id={labelId} className="field-row__label t-label">
          {label}
          {mine ? (
            <span className="field-row__mine t-meta" title={mineLabel}>
              ·
            </span>
          ) : null}
        </span>
      ) : (
        <label id={labelId} htmlFor={htmlFor} className="field-row__label t-label">
          {label}
          {mine ? (
            <span className="field-row__mine t-meta" title={mineLabel}>
              ·
            </span>
          ) : null}
        </label>
      )}

      <div className="field-row__control">
        {children}
        {error ? (
          <span id={errorId} className="field-row__error t-meta">
            {error}
          </span>
        ) : null}
        {consequence}
      </div>
    </div>
  )
}
